import React from "react";
import { Document, Page, View, Text, StyleSheet } from "@react-pdf/renderer";
import { ReportHeader } from "../components/charting/pdf/shared/ReportHeader";
import { ReportFooter } from "../components/charting/pdf/shared/ReportFooter";
import { ReportTable } from "../components/charting/pdf/shared/ReportTable";
import { StrikeZoneGrid } from "../components/charting/pdf/shared/StrikeZoneGrid";
import { reportStyles } from "../components/charting/pdf/shared/styles";
import { getPitchers, formatPitchType, isInStrikeZone } from "../components/charting/pdf/shared/utils";

const styles = StyleSheet.create({
  sequencesContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 10,
    gap: 10,
  },
  sequenceTable: {
    flex: 1,
    borderWidth: 1,
    borderColor: "#000000",
  },
  valueText: {
    fontSize: 8,
  },
});

export const PitcherScoutingReport = ({ charts = [] }) => {
  const pitchers = getPitchers(charts);

  if (!charts.length || !pitchers.length) {
    return (
      <Document>
        <Page size="A4" style={reportStyles.pageCompact}>
          <Text>No data available to generate the report.</Text>
        </Page>
      </Document>
    );
  }

  const pages = pitchers.map((pitcherName) => {
    const pitcherPitches = charts.flatMap((chart) =>
      chart.pitches.filter((pitch) => pitch.pitcher?.name === pitcherName)
    );

    const atBats = groupPitchesByAtBat(pitcherPitches);
    const markedPitches = markFirstPitches(pitcherPitches, atBats);

    const lhhPitches = markedPitches.filter(
      (pitch) => pitch.batter?.batHand?.toLowerCase() === "left"
    );
    const rhhPitches = markedPitches.filter(
      (pitch) => pitch.batter?.batHand?.toLowerCase() === "right"
    );

    const metrics = processPitchData(markedPitches);
    const sequencesRHH = findTopSequences(rhhPitches);
    const sequencesLHH = findTopSequences(lhhPitches);

    const tableRows = Object.entries(metrics).map(([type, data]) => ({
      cells: [
        { value: formatPitchType(type) },
        { value: data.avgVelo },
        { value: `LHH: ${data.usageLHH}% | RHH: ${data.usageRHH}%` },
        { value: `LHH: ${data.firstPitchRateLHH}% | RHH: ${data.firstPitchRateRHH}%` },
        { value: `LHH: ${data.zoneRateLHH}% | RHH: ${data.zoneRateRHH}%` },
        { value: `LHH: ${data.chaseRateLHH}% | RHH: ${data.chaseRateRHH}%` },
      ],
      cellStyles: Array(6).fill({ fontSize: 8 }),
    }));

    const strikeZoneData = Object.entries(metrics).reduce(
      (acc, [type, data]) => {
        acc[type] = { pitches: data.pitches };
        return acc;
      },
      {}
    );

    return (
      <Page key={pitcherName} size="A4" style={reportStyles.pageCompact}>
        <ReportHeader
          title={`${pitcherName} - Advance Report`}
          compact={true}
        />

        <ReportTable
          headers={[
            "Pitch Type",
            "Velo",
            "Overall Usage",
            "First Pitch",
            "In-Zone %",
            "Chase %",
          ]}
          rows={tableRows}
          compact={true}
          cellWidths={["15%", "10%", "20%", "20%", "17.5%", "17.5%"]}
        />

        <StrikeZoneGrid
          pitchData={strikeZoneData}
          compact={true}
          containerWidth="30%"
        />

        <View style={styles.sequencesContainer}>
          <View style={[styles.sequenceTable, { marginRight: 5 }]}>
            <View style={reportStyles.tableHeader}>
              <Text style={[reportStyles.tableCellCompact, { width: "100%" }]}>
                Top 3 Two-Pitch Sequences vs RHH
              </Text>
            </View>
            <View style={reportStyles.tableRowCompact}>
              <Text style={[reportStyles.tableCellCompact, { width: "25%" }]}>Sequence</Text>
              <Text style={[reportStyles.tableCellCompact, { width: "15%" }]}>Usage</Text>
              <Text style={[reportStyles.tableCellCompact, { width: "15%" }]}>Swing</Text>
              <Text style={[reportStyles.tableCellCompact, { width: "15%" }]}>Whiff</Text>
              <Text style={[reportStyles.tableCellCompact, { width: "15%" }]}>Chase</Text>
              <Text style={[reportStyles.tableCellCompact, { width: "15%" }]}>BIP</Text>
            </View>
            {sequencesRHH.map((seq, i) => (
              <View key={i} style={reportStyles.tableRowCompact}>
                <Text style={[reportStyles.tableCellCompact, { width: "25%" }]}>
                  {seq.key}
                </Text>
                <Text style={[reportStyles.tableCellCompact, { width: "15%" }]}>
                  {seq.usage}%
                </Text>
                <Text style={[reportStyles.tableCellCompact, { width: "15%" }]}>
                  {seq.swingRate}%
                </Text>
                <Text style={[reportStyles.tableCellCompact, { width: "15%" }]}>
                  {seq.whiffRate}%
                </Text>
                <Text style={[reportStyles.tableCellCompact, { width: "15%" }]}>
                  {seq.chaseRate}%
                </Text>
                <Text style={[reportStyles.tableCellCompact, { width: "15%" }]}>
                  {seq.bipRate}%
                </Text>
              </View>
            ))}
            {sequencesRHH.length === 0 && (
              <View style={reportStyles.tableRowCompact}>
                <Text style={[reportStyles.tableCellCompact, { width: "100%" }]}>
                  No data available
                </Text>
              </View>
            )}
          </View>

          <View style={styles.sequenceTable}>
            <View style={reportStyles.tableHeader}>
              <Text style={[reportStyles.tableCellCompact, { width: "100%" }]}>
                Top 3 Two-Pitch Sequences vs LHH
              </Text>
            </View>
            <View style={reportStyles.tableRowCompact}>
              <Text style={[reportStyles.tableCellCompact, { width: "25%" }]}>Sequence</Text>
              <Text style={[reportStyles.tableCellCompact, { width: "15%" }]}>Usage</Text>
              <Text style={[reportStyles.tableCellCompact, { width: "15%" }]}>Swing</Text>
              <Text style={[reportStyles.tableCellCompact, { width: "15%" }]}>Whiff</Text>
              <Text style={[reportStyles.tableCellCompact, { width: "15%" }]}>Chase</Text>
              <Text style={[reportStyles.tableCellCompact, { width: "15%" }]}>BIP</Text>
            </View>
            {sequencesLHH.map((seq, i) => (
              <View key={i} style={reportStyles.tableRowCompact}>
                <Text style={[reportStyles.tableCellCompact, { width: "25%" }]}>
                  {seq.key}
                </Text>
                <Text style={[reportStyles.tableCellCompact, { width: "15%" }]}>
                  {seq.usage}%
                </Text>
                <Text style={[reportStyles.tableCellCompact, { width: "15%" }]}>
                  {seq.swingRate}%
                </Text>
                <Text style={[reportStyles.tableCellCompact, { width: "15%" }]}>
                  {seq.whiffRate}%
                </Text>
                <Text style={[reportStyles.tableCellCompact, { width: "15%" }]}>
                  {seq.chaseRate}%
                </Text>
                <Text style={[reportStyles.tableCellCompact, { width: "15%" }]}>
                  {seq.bipRate}%
                </Text>
              </View>
            ))}
            {sequencesLHH.length === 0 && (
              <View style={reportStyles.tableRowCompact}>
                <Text style={[reportStyles.tableCellCompact, { width: "100%" }]}>
                  No data available
                </Text>
              </View>
            )}
          </View>
        </View>
        <ReportFooter />
      </Page>
    );
  });

  return <Document>{pages}</Document>;
};


const groupPitchesByAtBat = (pitches) => {
  const atBats = {};

  const sortedPitches = [...pitches].sort(
    (a, b) => new Date(a.time).getTime() - new Date(b.time).getTime()
  );

  let currentPitcherId = null;
  let currentBatterId = null;
  let currentInning = null;
  let currentTopBottom = null;
  let currentAtBatId = null;

  for (const pitch of sortedPitches) {
    const pitcherId = pitch.pitcher?.name;
    const batterId = pitch.batter?.name;
    const inning = pitch.inning;
    const topBottom = pitch.topBottom;

    if (!pitcherId || !batterId) continue;

    const isNewBatterPitcherCombo =
      pitcherId !== currentPitcherId || batterId !== currentBatterId;

    const isNewInning =
      inning !== currentInning || topBottom !== currentTopBottom;

    if (isNewBatterPitcherCombo || isNewInning || isNewAtBat(pitch)) {
      currentAtBatId = `${pitcherId}_${batterId}_${inning}_${topBottom}_${Date.now()}`;
      currentPitcherId = pitcherId;
      currentBatterId = batterId;
      currentInning = inning;
      currentTopBottom = topBottom;
    }

    if (!atBats[currentAtBatId]) {
      atBats[currentAtBatId] = [];
    }

    atBats[currentAtBatId].push(pitch.id);
  }

  return atBats;
};

const isNewAtBat = (pitch) => {
  const result = pitch.result?.toLowerCase();

  return (
    [
      "strikeout_swinging",
      "strikeout_looking",
      "walk",
      "hit_by_pitch",
      "in_play",
    ].includes(result) ||
    (pitch.hitDetails && pitch.hitDetails.result)
  );
};

const markFirstPitches = (pitches, atBats) => {
  return pitches.map((pitch) => {
    const isFirstPitch = Object.values(atBats).some(
      (pitchIds) => pitchIds[0] === pitch.id
    );

    return {
      ...pitch,
      isFirstPitch,
    };
  });
};

const processPitchData = (pitches) => {
  const totals = pitches.reduce(
    (acc, pitch) => {
      const isLHH = pitch.batter?.batHand?.toLowerCase() === "left";

      if (isLHH) {
        acc.totalLHH++;
        if (pitch.isFirstPitch === true) acc.totalFirstPitchLHH++;
      } else if (pitch.batter?.batHand?.toLowerCase() === "right") {
        acc.totalRHH++;
        if (pitch.isFirstPitch === true) acc.totalFirstPitchRHH++;
      }
      return acc;
    },
    { totalLHH: 0, totalRHH: 0, totalFirstPitchLHH: 0, totalFirstPitchRHH: 0 }
  );

  return pitches.reduce((acc, pitch) => {
    const type = pitch.type?.toUpperCase() || "UNKNOWN";
    const isLHH = pitch.batter?.batHand?.toLowerCase() === "left";
    const isRHH = pitch.batter?.batHand?.toLowerCase() === "right";

    if (!isLHH && !isRHH) return acc;

    if (!acc[type]) {
      acc[type] = {
        velocities: [],
        pitches: [],
        countLHH: 0,
        countRHH: 0,
        firstPitchLHH: 0,
        firstPitchRHH: 0,
        zonePitchesLHH: 0,
        zonePitchesRHH: 0,
        chasePitchesLHH: 0,
        chasePitchesRHH: 0,
      };
    }

    const data = acc[type];
    data.pitches.push(pitch);

    if (isLHH) {
      data.countLHH++;
      if (pitch.isFirstPitch === true) data.firstPitchLHH++;
    } else if (isRHH) {
      data.countRHH++;
      if (pitch.isFirstPitch === true) data.firstPitchRHH++;
    }

    if (pitch.velocity) {
      const velocity = Number(pitch.velocity);
      if (!isNaN(velocity)) data.velocities.push(velocity);
    }

    const x = pitch.location?.x ?? pitch.x;
    const y = pitch.location?.y ?? pitch.y;
    const isInZone = isInStrikeZone(x, y);
    const isSwing = [
      "swinging_strike",
      "foul",
      "in_play",
      "strikeout_swinging",
    ].includes(pitch.result);
    const isChase = !isInZone && isSwing;

    if (isLHH) {
      if (isInZone) data.zonePitchesLHH++;
      if (isChase) data.chasePitchesLHH++;
    } else if (isRHH) {
      if (isInZone) data.zonePitchesRHH++;
      if (isChase) data.chasePitchesRHH++;
    }

    data.avgVelo = data.velocities.length
      ? (
          data.velocities.reduce((a, b) => a + b) / data.velocities.length
        ).toFixed(1)
      : "-";

    data.usageLHH =
      totals.totalLHH > 0
        ? ((data.countLHH / totals.totalLHH) * 100).toFixed(1)
        : "0.0";
    data.usageRHH =
      totals.totalRHH > 0
        ? ((data.countRHH / totals.totalRHH) * 100).toFixed(1)
        : "0.0";

    data.firstPitchRateLHH =
      totals.totalFirstPitchLHH > 0
        ? Number(
            ((data.firstPitchLHH / totals.totalFirstPitchLHH) * 100).toFixed(1)
          )
        : 0;
    data.firstPitchRateRHH =
      totals.totalFirstPitchRHH > 0
        ? Number(
            ((data.firstPitchRHH / totals.totalFirstPitchRHH) * 100).toFixed(1)
          )
        : 0;

    data.zoneRateLHH =
      data.countLHH > 0
        ? ((data.zonePitchesLHH / data.countLHH) * 100).toFixed(1)
        : "0.0";
    data.zoneRateRHH =
      data.countRHH > 0
        ? ((data.zonePitchesRHH / data.countRHH) * 100).toFixed(1)
        : "0.0";

    data.chaseRateLHH =
      data.countLHH > 0
        ? ((data.chasePitchesLHH / data.countLHH) * 100).toFixed(1)
        : "0.0";
    data.chaseRateRHH =
      data.countRHH > 0
        ? ((data.chasePitchesRHH / data.countRHH) * 100).toFixed(1)
        : "0.0";

    return acc;
  }, {});
};

const findTopSequences = (pitches) => {
  if (pitches.length < 2) {
    return [];
  }

  const sequences = [];
  const pitchesByAtBat = {};

  for (const pitch of pitches) {
    const atBatId = `${pitch.batter?.name}_${pitch.inning}_${pitch.topBottom}`;

    if (!pitchesByAtBat[atBatId]) {
      pitchesByAtBat[atBatId] = [];
    }

    pitchesByAtBat[atBatId].push(pitch);
  }

  Object.values(pitchesByAtBat).forEach((atBatPitches) => {
    atBatPitches.sort((a, b) => new Date(a.time) - new Date(b.time));

    for (let i = 0; i < atBatPitches.length - 1; i++) {
      const currentPitch = atBatPitches[i];
      const nextPitch = atBatPitches[i + 1];

      const sequence = {
        key: `${currentPitch.type}-${nextPitch.type}`,
        firstPitch: currentPitch,
        secondPitch: nextPitch,
      };

      sequences.push(sequence);
    }
  });

  const groupedSequences = sequences.reduce((acc, seq) => {
    if (!acc[seq.key]) {
      acc[seq.key] = {
        key: seq.key,
        count: 0,
        sequences: [],
      };
    }
    acc[seq.key].count++;
    acc[seq.key].sequences.push(seq);
    return acc;
  }, {});

  return Object.values(groupedSequences)
    .sort((a, b) => b.count - a.count)
    .slice(0, 3)
    .map((group) => {
      const totalSequences = group.count;

      const secondPitches = group.sequences.map((seq) => seq.secondPitch);

      const swings = secondPitches.filter(
        (p) =>
          p.result === "swinging_strike" ||
          p.result === "foul" ||
          p.result === "in_play" ||
          p.result === "strikeout_swinging"
      ).length;

      const whiffs = secondPitches.filter(
        (p) =>
          p.result === "swinging_strike" || p.result === "strikeout_swinging"
      ).length;

      const chases = secondPitches.filter(
        (p) => {
          const x = p.location?.x ?? p.x;
          const y = p.location?.y ?? p.y;
          return (
            !isInStrikeZone(x, y) &&
          (p.result === "swinging_strike" ||
            p.result === "foul" ||
            p.result === "strikeout_swinging" ||
            p.result === "in_play")
          );
        }
      ).length;

      const bip = secondPitches.filter((p) => p.result === "in_play").length;

      return {
        key: group.key,
        usage: ((group.count / sequences.length) * 100).toFixed(1),
        swingRate: ((swings / totalSequences) * 100).toFixed(1),
        whiffRate: ((whiffs / totalSequences) * 100).toFixed(1),
        chaseRate: ((chases / totalSequences) * 100).toFixed(1),
        bipRate: ((bip / totalSequences) * 100).toFixed(1),
      };
    });
};


